<section class="space-y-6" role="region" aria-label="Stores">
  <div
    class="hero overflow-hidden rounded-3xl border border-base-200 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/10 shadow-md"
    role="banner"
  >
    <div
      class="hero-content w-full max-w-none flex-col items-stretch gap-6 p-4 sm:p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8"
    >
      <div class="space-y-3">
        <div
          class="badge badge-primary badge-outline gap-2 px-3 py-3 text-xs font-semibold shadow-sm"
        >
          <span
            class="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"
          ></span>
          Operations
        </div>

        <div class="space-y-1">
          <h1 class="text-3xl font-black tracking-tight sm:text-4xl">Stores</h1>
          <p class="max-w-2xl text-base font-medium text-base-content/80">
            Manage store coverage, coordinates, and activation status.
          </p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button
            class="btn btn-primary gap-2 w-full sm:w-auto"
            type="button"
            (click)="navigate('/stores/add')"
          >
            <i class="fa-solid fa-plus"></i>
            Add Store
          </button>
          <button
            class="btn btn-primary btn-outline gap-2 w-full sm:w-auto"
            type="button"
            (click)="loadStores()"
          >
            <i class="fa-solid fa-arrow-rotate-right"></i>
            Refresh
          </button>
        </div>
      </div>

      <div class="grid min-w-0 gap-4 sm:grid-cols-2 lg:w-[32rem]">
        <div
          class="rounded-2xl border border-base-200 bg-base-100/80 backdrop-blur p-5 shadow-sm"
        >
          <div class="flex items-center justify-between">
            <p
              class="text-xs uppercase tracking-widest text-base-content/50 font-semibold"
            >
              Total
            </p>
            <i class="fa-solid fa-store text-primary/60"></i>
          </div>
          <p class="mt-3 text-3xl font-black">{{ totalCount }}</p>
          <p class="mt-2 text-sm text-base-content/60">Stores in catalog</p>
        </div>
        <div
          class="rounded-2xl border border-base-200 bg-base-100/80 backdrop-blur p-5 shadow-sm"
        >
          <div class="flex items-center justify-between">
            <p
              class="text-xs uppercase tracking-widest text-base-content/50 font-semibold"
            >
              Active
            </p>
            <i class="fa-solid fa-circle-check text-success/60"></i>
          </div>
          <p class="mt-3 text-3xl font-black text-success">
            {{ activeCount ? activeCount : 0 }}
          </p>
          <p class="mt-2 text-sm text-base-content/60">Currently operational</p>
        </div>
      </div>
    </div>
  </div>

  <div class="card border border-base-200 bg-base-100 shadow-sm">
    <div class="card-body gap-4">
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_10rem_10rem]">
        <div>
          <label class="label" for="store-search">
            <span class="label-text font-semibold text-sm">Search</span>
          </label>
          <label class="input input-bordered flex items-center gap-3 w-full">
            <i class="fa-solid fa-magnifying-glass text-base-content/40"></i>
            <input
              id="store-search"
              type="text"
              class="grow"
              placeholder="Search by store name or code..."
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearchChange()"
            />
            @if (searchTerm) {
            <button
              class="btn btn-ghost btn-xs btn-circle"
              type="button"
              (click)="searchTerm = ''; onSearchChange()"
              aria-label="Clear search"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
            }
          </label>
        </div>

        <div>
          <label class="label" for="store-status"><span class="label-text font-semibold text-sm">Status</span></label>
          <select id="store-status" class="select select-bordered w-full" [(ngModel)]="status" (ngModelChange)="page = 1; loadStores()">
            <option value="active">Active</option><option value="inactive">Inactive</option><option value="all">All</option>
          </select>
          <label class="label" for="page-size">
            <span class="label-text font-semibold text-sm">Page size</span>
          </label>
          <select
            id="page-size"
            class="select select-bordered w-full"
            [(ngModel)]="pageSize"
            (ngModelChange)="page = 1; loadStores()"
          >
            <option [ngValue]="10">10</option>
            <option [ngValue]="20">20</option>
            <option [ngValue]="50">50</option>
          </select>
        </div>

        <div>
          <label class="label" for="sort-name">
            <span class="label-text font-semibold text-sm">Sort</span>
          </label>
          <button
            id="sort-name"
            class="btn btn-outline w-full gap-2 justify-between"
            type="button"
            (click)="toggleSort()"
          >
            <span>Name</span>
            <i
              class="fa-solid"
              [ngClass]="{
                'fa-arrow-up': order === 'ASC',
                'fa-arrow-down': order === 'DESC'
              }"
            ></i>
          </button>
        </div>
      </div>

      @if (loading) {
      <div class="flex items-center gap-3 py-8">
        <span class="loading loading-spinner loading-md"></span>
        <span class="font-semibold">Loading stores…</span>
      </div>
      } @else {
      <div class="overflow-x-auto rounded-xl border border-base-200">
        <table class="table">
          <thead class="bg-base-200/50">
            <tr>
              <th>Store</th>
              <th>Code</th>
              <th>City</th>
              <th>Coordinates</th>
              <th>Delivery area</th>
              <th>Status</th>
              <th class="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (store of stores; track store.id) {
            <tr class="hover">
              <td class="font-semibold">{{ store.name }}</td>
              <td class="text-sm text-base-content/70">
                {{ store.store_code }}
              </td>
              <td class="text-sm text-base-content/70">{{ store.city }}</td>
              <td class="text-sm text-base-content/70">
                {{ store.latitude ?? '--' }}, {{ store.longitude ?? '--' }}
              </td>
              <td class="text-sm text-base-content/70">
                @if (getDeliveryArea(store).length >= 3) {
                <span class="badge badge-success badge-outline">
                  Custom · {{ getDeliveryArea(store).length }} points
                </span>
                } @else if (store.store_radius > 0) {
                <span class="badge badge-warning badge-outline">
                  Legacy · {{ store.store_radius }} km
                </span>
                } @else {
                <span>--</span>
                }
              </td>
              <td>
                <button
                  type="button"
                  class="btn btn-ghost btn-xs"
                  (click)="toggleStoreStatus(store)"
                  [attr.aria-label]="'Toggle status for ' + store.name"
                >
                  <span
                    class="badge badge-outline"
                    [class.badge-success]="store.is_active && !isClosedNow(store)"
                    [class.badge-error]="store.is_active && isClosedNow(store)"
                    [class.badge-ghost]="!store.is_active"
                  >
                    {{
                      !store.is_active
                        ? 'Inactive'
                        : isClosedNow(store)
                          ? 'Closed now'
                          : 'Active'
                    }}
                  </span>
                </button>
              </td>
              <td class="text-right">
                <div class="flex justify-end gap-2">
                  <button
                    class="btn btn-ghost btn-sm btn-square text-primary"
                    type="button"
                    (click)="downloadDeliveryMap(store)"
                    [disabled]="downloadingStoreId === store.id"
                    [attr.aria-label]="'Download delivery map for ' + store.name"
                    title="Download delivery area map"
                  >
                    @if (downloadingStoreId === store.id) {
                    <span class="loading loading-spinner loading-xs"></span>
                    } @else {
                    <i class="fa-solid fa-map-location-dot"></i>
                    }
                  </button>
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    type="button"
                    (click)="editStore(store.id)"
                    aria-label="Edit store"
                  >
                    <i class="fa-solid fa-pen"></i>
                  </button>
                  <button
                    class="btn btn-ghost btn-sm btn-square text-error"
                    type="button"
                    (click)="deleteStore(store)"
                    aria-label="Delete store"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            } @if (stores.length === 0) {
            <tr>
              <td colspan="7">
                <div class="py-12 text-center text-base-content/60">
                  No stores found.
                </div>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      @if (totalPages > 1) {
      <div class="flex items-center justify-between pt-2">
        <p class="text-sm text-base-content/60">
          Page {{ page }} of {{ totalPages }} · {{ totalCount }} total
        </p>
        <div class="join" role="group" aria-label="Pagination">
          <button
            class="join-item btn btn-sm"
            type="button"
            [disabled]="page === 1"
            (click)="changePage(page - 1)"
          >
            <i class="fa-solid fa-angle-left"></i>
          </button>
          <button
            class="join-item btn btn-sm"
            type="button"
            [disabled]="page === totalPages"
            (click)="changePage(page + 1)"
          >
            <i class="fa-solid fa-angle-right"></i>
          </button>
        </div>
      </div>
      } }
    </div>
  </div>
</section>
